import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';

const CrudTab = ({ state }) => {
  const { students, setStudents, tracking, changes, setChanges, setSqlOutput, setCsharpOutput } = state;
  const ensureTracking = () => {
    if (!tracking) {
      toast.warning("Tracking is disabled for Create-Update-Delete Operations.");
      setSqlOutput("Tracking Disabled: Action Blocked");
      setCsharpOutput("// Tracking Disabled: Action Blocked");
      return false;
    }
    return true;
  };

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', enrollmentDate: '', status: 'Active' });

  const addStudent = () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    const newStudent = {
      id: Math.max(...students.map(s => s.id), 0) + 1,
      ...formData,
      enrollmentDate: formData.enrollmentDate || new Date().toISOString().split('T')[0]
    };

    setStudents([...students, newStudent]);
    setChanges({ ...changes, added: changes.added + 1 });
    
    const sql = `INSERT INTO Students (Name, Email, EnrollmentDate, Status)\nVALUES ('${formData.name}', '${formData.email}', '${newStudent.enrollmentDate}', '${formData.status}');`;
    setSqlOutput(sql);
    
    toast.success('Student added successfully');
    setFormData({ name: '', email: '', enrollmentDate: '', status: 'Active' });
    setIsAddOpen(false);
    setCsharpOutput(
        `// Create\nvar student = new Student {\n` +
        `  Name = "${formData.name}",\n` +
        `  Email = "${formData.email}",\n` +
        `  EnrollmentDate = DateOnly.Parse("${newStudent.enrollmentDate}"),\n` +
        `  Status = "${formData.status}"\n` +
        `};\n\ncontext.Students.Add(student);\nawait context.SaveChangesAsync();`
    );

  };

  const updateStudent = () => {
    if (!ensureTracking()) return;
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    setStudents(students.map(s => 
      s.id === editingStudent.id ? { ...s, ...formData } : s
    ));
    setChanges({ ...changes, modified: changes.modified + 1 });
    
    const sql = `UPDATE Students\nSET Name = '${formData.name}', Email = '${formData.email}', Status = '${formData.status}'\nWHERE Id = ${editingStudent.id};`;
    setSqlOutput(sql);
    
    toast.success('Student updated successfully');
    setEditingStudent(null);
    setIsEditOpen(false);
    setCsharpOutput(
        `// Update\nvar student = await context.Students.FindAsync(${editingStudent.id});\n` +
        `student.Name = "${formData.name}";\n` +
        `student.Email = "${formData.email}";\n` +
        `student.Status = "${formData.status}";\n\n` +
        `await context.SaveChangesAsync();`
    );
  };

  const deleteStudent = (id) => {
    if (!ensureTracking()) return;
    setStudents(students.filter(s => s.id !== id));
    setChanges({ ...changes, deleted: changes.deleted + 1 });
    
    const sql = `DELETE FROM Students\nWHERE Id = ${id};`;
    setSqlOutput(sql);
    
    toast.success('Student deleted successfully');
    setCsharpOutput(
        `// Delete\nvar student = await context.Students.FindAsync(${id});\n` +
        `context.Students.Remove(student);\nawait context.SaveChangesAsync();`
    );
  };

  const openEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      email: student.email,
      enrollmentDate: student.enrollmentDate,
      status: student.status
    });
    setIsEditOpen(true);
  };

  return (
    <div className="space-y-6 pt-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold">CRUD Operations</h3>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>• Create: Add new entities to context</li>
          <li>• Read: Query existing data</li>
          <li>• Update: Modify entity properties</li>
          <li>• Delete: Remove entities from database</li>
        </ul>
      </div>

      <div className="space-y-2">
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              Add Student
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Student</DialogTitle>
              <DialogDescription>
                Add a new student record to the database
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="add-name">Name</Label>
                <Input
                  id="add-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label htmlFor="add-date">Enrollment Date</Label>
                <Input
                  id="add-date"
                  type="date"
                  value={formData.enrollmentDate}
                  onChange={(e) => setFormData({ ...formData, enrollmentDate: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button onClick={addStudent}>Add Student</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Student</DialogTitle>
              <DialogDescription>
                Update student information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button onClick={updateStudent}>Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Current Students</h4>
        <div className="max-h-96 space-y-2 overflow-y-auto">
          <AnimatePresence>
            {students.map((student) => (
              <motion.div
                key={student.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between rounded-lg border border-border bg-surface-elevated p-3"
              >
                <div className="flex-1">
                  <p className="font-medium">{student.name}</p>
                  <p className="text-xs text-muted-foreground">{student.email}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(student)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteStudent(student.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CrudTab;